import React, { useMemo, useState, memo, useContext } from "react";
import { useStyles } from "./styles";
import { config, formatPercent } from "../../helpers/constants";
import { AppContext } from "../../AppContext";

export default memo(function SimilarityTable({ comparison, onCellHover, onSourceClick, threshold }) {
    const classes = useStyles();
    const state = useContext(AppContext);
    const tc = (state.strings || {}).textComparisons || {};
    const [lo, hi] = Array.isArray(threshold) ? threshold : [0, threshold ?? 0];
    const [sortKey, setSortKey] = useState("percent");
    const [sortAsc, setSortAsc] = useState(false); // percent descending by default

    const handleSortClick = (key) => {
        if (key === sortKey) {
            setSortAsc(a => !a);
        } else {
            setSortKey(key);
            setSortAsc(key !== "percent" && key !== "percent2"); // percent columns start desc; text columns start asc
        }
    };

    const rows = useMemo(() => {
        const filtered = comparison.sources.filter(
            src => src.percent !== null && src.percent >= lo && src.percent <= hi
        );
        return [...filtered].sort((a, b) => {
            let cmp;
            if (sortKey === "percent") cmp = (a.percent ?? 0) - (b.percent ?? 0);
            else if (sortKey === "percent2") cmp = (a.percent2 ?? a.percent ?? 0) - (b.percent2 ?? b.percent ?? 0);
            else if (sortKey === "text1") cmp = a.text1.name.localeCompare(b.text1.name);
            else cmp = a.text2.name.localeCompare(b.text2.name);
            return sortAsc ? cmp : -cmp;
        });
    }, [comparison.sources, lo, hi, sortKey, sortAsc]);

    const sortIndicator = (key) => (
        <span style={{ opacity: key === sortKey ? 1 : 0, marginRight: 4 }}>
            {sortAsc ? "🔼" : "🔽"}
        </span>
    );

    const pctBadge = (pct) => {
        const p = formatPercent(Math.max(0, Math.min(pct ?? 0, 100)));
        const n = (p && parseInt(p)) || 0;
        return (
            <div style={{ textAlign: "center" }}>
                <img src={`${config.backendUrl}button?percent=${n}&size=0`} alt={`${n}%`} />
                <span style={{ display: "block", fontSize: 12, marginTop: -21 }}>{p ? `${p}%` : ""}</span>
            </div>
        );
    };

    const wordsFmt = (w) => w != null ? `${w.toLocaleString()} ${tc.words || "w"}` : null;

    return (
        <div className={classes.tableScroll}>
            <table className={classes.table}>
                <thead>
                    <tr>
                        <th
                            className={classes.thCenter}
                            onClick={() => handleSortClick("percent")}
                            style={{ cursor: "pointer", userSelect: "none", width: 80 }}
                        >
                            <span style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                {sortIndicator("percent")}<span>{tc.colPctT1 || "% T1"}</span>
                            </span>
                        </th>
                        <th
                            className={classes.th}
                            onClick={() => handleSortClick("text1")}
                            style={{ cursor: "pointer", userSelect: "none" }}
                        >
                            <span style={{ display: "flex", alignItems: "center" }}>
                                {sortIndicator("text1")}<span>{tc.colText1 || "Text 1"}</span>
                            </span>
                        </th>
                        <th
                            className={classes.thCenter}
                            onClick={() => handleSortClick("percent2")}
                            style={{ cursor: "pointer", userSelect: "none", width: 80 }}
                        >
                            <span style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                {sortIndicator("percent2")}<span>{tc.colPctT2 || "% T2"}</span>
                            </span>
                        </th>
                        <th
                            className={classes.th}
                            onClick={() => handleSortClick("text2")}
                            style={{ cursor: "pointer", userSelect: "none" }}
                        >
                            <span style={{ display: "flex", alignItems: "center" }}>
                                {sortIndicator("text2")}<span>{tc.colText2 || "Text 2"}</span>
                            </span>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((src) => (
                        <tr
                            key={src.id}
                            className={classes.row}
                            onClick={() => onSourceClick && onSourceClick(src)}
                            onMouseEnter={() => onCellHover && onCellHover({
                                val: src.percent,
                                val2: src.percent2,
                                mw: src.mw,
                                rowName: src.text1.name,
                                colName: src.text2.name,
                                rowWords: src.text1.words,
                                colWords: src.text2.words,
                            })}
                            onMouseLeave={() => onCellHover && onCellHover(null)}
                        >
                            <td className={classes.tdCenter} style={{ width: 80 }}>
                                {pctBadge(src.percent)}
                            </td>
                            <td className={classes.td}>
                                {src.text1.name}
                                {wordsFmt(src.text1.words) && (
                                    <span style={{ marginLeft: 6, color: "#999", fontSize: 12 }}>
                                        {wordsFmt(src.text1.words)}
                                    </span>
                                )}
                            </td>
                            <td className={classes.tdCenter} style={{ width: 80 }}>
                                {pctBadge(src.percent2 ?? src.percent)}
                            </td>
                            <td className={classes.td}>
                                {src.text2.name}
                                {wordsFmt(src.text2.words) && (
                                    <span style={{ marginLeft: 6, color: "#999", fontSize: 12 }}>
                                        {wordsFmt(src.text2.words)}
                                    </span>
                                )}
                            </td>
                        </tr>
                    ))}
                    {rows.length === 0 && (
                        <tr>
                            <td colSpan={4} style={{ textAlign: "center", padding: 24, color: "#999" }}>
                                {tc.noSimilaritiesInRange || "No similarities in this range"}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
});
